import {
  VStack,
  HStack,
  Divider,
  useToast,
  Tag,
  TagLabel,
  Box,
  TagLeftIcon,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useEffect, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  GetTasksBySectionDocumentGQLType,
  useGetOrganizationQuery,
  useGetTaskByIdQuery,
  useUpdateTaskPropertiesMutation,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  getMaxDomainDueDateForTask,
  statusColorScheme,
  statusIcon,
} from './common';
import { Icon } from 'packages/common-frontend/components/icons';
import { SearchSelect } from 'packages/common-frontend/components/SearchSelect';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

export const TaskDetails = ({ taskId }: { taskId: string }) => {
  const toast = useToast();

  const getTaskQuery = useGetTaskByIdQuery({
    variables: { taskId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const taskDueDateAsJsDate = useMemo(() => {
    if (getTaskQuery.data?.getTaskById) {
      return commonDate.fullDateStrToDate(
        getTaskQuery.data.getTaskById.taskEntity.dueDate,
      );
    }

    return null;
  }, [getTaskQuery.data]);

  // polling getTaskQuery while component is mounted.
  // specifically in the case a different user updates a task,
  // we want to reliably show those update in a _somewhat_ quick fashion (30 secs)
  useEffect(() => {
    getTaskQuery.startPolling(30000);
    return () => {
      getTaskQuery.stopPolling();
    };
  }, [getTaskQuery]);

  const [
    updateTaskPropertiesMutation,
    { loading: updateTaskLoading },
  ] = useUpdateTaskPropertiesMutation({
    refetchQueries: [GetTasksBySectionDocumentGQLType],
  });

  if (getTaskQuery.error) {
    throw getTaskQuery.error;
  }

  const {
    user: { organizationId, roleType },
  } = useUserAuthOrThrow();

  const showTaskUserAssignment = roleType === 'ADMIN';

  const getOrganization = useGetOrganizationQuery({
    skip: showTaskUserAssignment === false,
    variables: { organizationId },
  });

  if (getOrganization.error) {
    throw getOrganization.error;
  }

  const userOptions = useMemo(() => {
    if (
      getOrganization.data?.organization &&
      getOrganization.data.organization.users.length > 0
    ) {
      return getOrganization.data.organization.users.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      }));
    }

    return null;
  }, [getOrganization.data?.organization]);

  if (!getTaskQuery.data?.getTaskById || !taskDueDateAsJsDate) {
    return null;
  }

  const task = getTaskQuery.data.getTaskById;
  const today = new Date();

  return (
    <>
      <Divider />
      <Typography variant="section-subtitle" trim>
        Task Details
      </Typography>
      <VStack w="100%" gap="1rem">
        <HStack justifyContent="space-between" w="100%">
          <Typography fontWeight="medium">Task Status</Typography>
          <Tag
            variant="ghost"
            colorScheme={statusColorScheme[task.taskEntity.status]}
          >
            <TagLeftIcon>
              <Icon icon={statusIcon[task.taskEntity.status]} />
            </TagLeftIcon>
            <TagLabel>{_.startCase(task.taskEntity.status)}</TagLabel>
          </Tag>
        </HStack>

        <HStack justifyContent="space-between" w="100%">
          <Typography fontWeight="medium">Task Due Date</Typography>
          <SimpleDatePicker
            width="50%"
            dateFormat="MMMM dd, yyyy"
            minDate={today < taskDueDateAsJsDate ? today : taskDueDateAsJsDate}
            maxDate={getMaxDomainDueDateForTask(task)}
            selectedDate={taskDueDateAsJsDate}
            disabled={
              updateTaskLoading ||
              getTaskQuery.loading ||
              task.taskEntity.status === 'completed' ||
              task.taskEntity.status === 'archived' ||
              // Per product, "MEMBER" users should not be able to update due date
              roleType === 'MEMBER'
            }
            showTimeSelect={false}
            isClearable={false}
            setSelectedDate={async (newDueDate: Date | null) => {
              if (newDueDate) {
                const newDueDateFullDateStr = commonDate.dateToFullDateString(
                  newDueDate,
                );

                if (task.taskEntity.dueDate !== newDueDateFullDateStr) {
                  try {
                    const result = await updateTaskPropertiesMutation({
                      variables: {
                        taskId: task.taskEntity.id,
                        newDueDate: newDueDateFullDateStr,
                      },
                    });
                    if (!result.data) {
                      throw Error(
                        `Failed to update task due date [taskId: ${task.taskEntity.id}]`,
                      );
                    }
                    await getTaskQuery.refetch();
                  } catch (err) {
                    Sentry.captureException(err);
                    toast({
                      title: 'Failed to update task due date.',
                      status: 'error',
                      position: 'top',
                    });
                  }
                }
              }
            }}
          />
        </HStack>

        <HStack justifyContent="space-between" w="100%">
          <Typography fontWeight="medium">Assigned To</Typography>
          {userOptions && showTaskUserAssignment ? (
            <Box width="50%">
              <SearchSelect
                value={userOptions.find(
                  (option) => option.value === task.taskEntity.assignedUser?.id,
                )}
                isDisabled={
                  updateTaskLoading ||
                  getTaskQuery.loading ||
                  task.taskEntity.status === 'completed' ||
                  task.taskEntity.status === 'archived'
                }
                onChange={async (newUserId) => {
                  if (newUserId !== task.taskEntity.assignedUser?.id) {
                    try {
                      const result = await updateTaskPropertiesMutation({
                        variables: {
                          taskId: task.taskEntity.id,
                          newAssignedUserId: newUserId,
                        },
                      });
                      if (!result.data) {
                        throw Error(
                          `Failed to update task user assignment [taskId: ${task.taskEntity.id}]`,
                        );
                      }
                      await getTaskQuery.refetch();
                    } catch (err) {
                      Sentry.captureException(err);
                      toast({
                        title: 'Failed to update task user assignment.',
                        status: 'error',
                        position: 'top',
                      });
                    }
                  }
                }}
                options={userOptions}
                isSelectedColor="lightgrey" // sarah todo: update SearchSelect component to operate on color scheme rather than  this
                isFocusedColor="lightgrey"
              />
            </Box>
          ) : (
            <Typography variant="base-muted">
              {task.taskEntity.assignedUser
                ? `${task.taskEntity.assignedUser.firstName} ${task.taskEntity.assignedUser.lastName}`
                : '-'}
            </Typography>
          )}
        </HStack>
      </VStack>
    </>
  );
};
